import { useDistricts, useProvinces } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { mapOptionObjectToArray } from "./Location/utils";

const useCustomHook = ({
  mainForm,
  isStepable = true,
  isClearDistrictAfterChooseProvince = true,
  isClearWardAfterChooseDistrict = true,
  is_level_2 = true,
}) => {
  const [provinceParams, setProvinceParams] = useState({
    countryId: 1,
    type: 1,
    parent_code: "",
    record_status: is_level_2 ? "O" : "C",
  });
  const [districtParams, setDistrictParams] = useState({
    countryId: 1,
    type: isStepable ? 1 : 2,
    parent_code: "",
    record_status: is_level_2 ? "O" : "C",
  });
  const [wardsParams, setWardsParams] = useState({
    countryId: 1,
    type: isStepable ? 1 : 3,
    parent_code: "",
    record_status: is_level_2 ? "O" : "C",
  });

  const { setValue, watch } = mainForm;

  const province_code = watch("province_code");
  const district_code = watch("district_code");

  useEffect(() => {
    const newRecordStatus = is_level_2 ? "O" : "C";
    setProvinceParams(prev => ({
      ...prev,
      record_status: newRecordStatus,
    }));
    setDistrictParams(prev => ({
      ...prev,
      record_status: newRecordStatus,
    }));
    setWardsParams(prev => ({
      ...prev,
      record_status: newRecordStatus,
    }));
  }, [is_level_2]);

  // PROVINCE SELECT HANDLE
  const { data: provinces, refetch: refetchProvinces } =
    useProvinces(provinceParams);

  useEffect(() => {
    if (provinceParams.type === 1) refetchProvinces();
  }, [provinceParams, refetchProvinces]);

  const provinceOptions = useMemo(() => {
    return mapOptionObjectToArray(provinces);
  }, [provinces]);

  // DISTRICT SELECT HANDLE
  const { data: districts, refetch: refetchDistricts } =
    useDistricts(districtParams);

  useEffect(() => {
    if (province_code) {
      if (isClearDistrictAfterChooseProvince) {
        setValue("district_code", null);
        setValue("ward_code", null);
      }
      if (!is_level_2) {
        setDistrictParams(prev => ({
          ...prev,
          type: 2,
          parent_code: province_code.value,
          record_status: "C",
        }));
      } else {
        setDistrictParams(prev => ({
          ...prev,
          type: 2,
          parent_code: province_code.value,
          record_status: "O",
        }));
      }
    } else {
      setDistrictParams(prev => ({
        ...prev,
        type: 2,
        parent_code: "",
      }));
      setWardsParams(prev => ({
        ...prev,
        type: 1,
        parent_code: "",
      }));
    }
  }, [province_code, isClearDistrictAfterChooseProvince, is_level_2, setValue]);

  useEffect(() => {
    if (districtParams.type === 2 && districtParams.parent_code) {
      refetchDistricts();
    }
  }, [districtParams, refetchDistricts]);

  const districtOptions = useMemo(() => {
    return mapOptionObjectToArray(districts);
  }, [districts]);

  const isDisableDistrictSelect = useMemo(() => {
    return !Boolean(province_code);
  }, [province_code]);

  // WARD SELECT HANDLE
  const { data: wards, refetch: refetchWards } = useDistricts(wardsParams);

  useEffect(() => {
    if (!is_level_2) {
      if (district_code) {
        if (isClearWardAfterChooseDistrict) {
          setValue("ward_code", null);
        }
        setWardsParams(prev => ({
          ...prev,
          type: 3,
          parent_code: district_code.value,
          record_status: "C",
        }));
      } else {
        setValue("ward_code", null);
        setWardsParams(prev => ({
          ...prev,
          type: 1,
          parent_code: "",
        }));
      }
    }
  }, [district_code, isClearWardAfterChooseDistrict, is_level_2, setValue]);

  useEffect(() => {
    if (!is_level_2 && wardsParams?.type === 3 && wardsParams?.parent_code)
      refetchWards();
  }, [is_level_2, wardsParams, refetchWards]);

  const wardOptions = useMemo(() => {
    if (is_level_2) return districtOptions;
    return mapOptionObjectToArray(wards);
  }, [is_level_2, districtOptions, wards]);

  const isDisableWardSelect = useMemo(() => {
    return is_level_2 ? !Boolean(province_code) : !Boolean(district_code);
  }, [is_level_2, province_code, district_code]);

  return {
    provinceOptions,
    districtOptions,
    isDisableDistrictSelect,
    wardOptions,
    isDisableWardSelect,
  };
};

export default useCustomHook;
